import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Image } from '@/shared/ui/image';

const commonStyles: React.CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};

interface MediaRendererProps {
  videoSrc?: string;
  imageSrc?: string;
  alt?: string;
  showVideo: boolean;
  hasVideoError: boolean;
  onVideoError?: () => void;
}

export const MediaRenderer: React.FC<MediaRendererProps> = ({
  videoSrc,
  imageSrc,
  alt = 'banner_image',
  showVideo,
  hasVideoError,
  onVideoError,
}) => {
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const shouldShowVideo = useMemo(
    () => showVideo && !hasVideoError && videoSrc,
    [showVideo, hasVideoError, videoSrc]
  );

  // Сброс состояний загрузки при изменении источников
  useEffect(() => {
    setIsVideoLoaded(false);
  }, [videoSrc]);

  useEffect(() => {
    setIsImageLoaded(false);
  }, [imageSrc]);

  // Обработка загрузки видео
  useEffect(() => {
    const video = videoRef.current;
    if (!shouldShowVideo || !video) return;

    const handleLoad = () => setIsVideoLoaded(true);
    const handleError = () => {
      setIsVideoLoaded(false);
      onVideoError?.();
    };

    video.addEventListener('loadeddata', handleLoad);
    video.addEventListener('error', handleError);
    video.load();

    return () => {
      video.removeEventListener('loadeddata', handleLoad);
      video.removeEventListener('error', handleError);
    };
  }, [shouldShowVideo, onVideoError]);

  const showImageFallback = !shouldShowVideo || !isVideoLoaded;

  return (
    <>
      {shouldShowVideo && (
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          style={{
            ...commonStyles,
            opacity: isVideoLoaded ? 1 : 0.5,
            transition: 'opacity 0.4s ease-in-out',
            position: shouldShowVideo ? 'relative' : 'absolute',
          }}
        >
          <source src={videoSrc} type="video/mp4" />
        </video>
      )}

      {showImageFallback && imageSrc && (
        <Image
          src={imageSrc}
          alt={alt}
          onLoad={() => setIsImageLoaded(true)}
          style={{
            ...commonStyles,
            opacity: isImageLoaded ? 1 : 0,
            transition: 'opacity 0.4s ease-in-out',
            position: shouldShowVideo ? 'absolute' : 'relative',
            top: 0,
            left: 0,
          }}
        />
      )}
    </>
  );
};
